import { useEffect, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/Button';
import { Icon } from '@/shared/components/Icon';
import { Sheet } from '@/shared/components/Sheet';
import { StatusBadge, type StatusTone } from '@/shared/components/StatusBadge';
import { PHONE_QUERY, useMediaQuery } from '@/shared/hooks/useMediaQuery';

import { ORIENTATION } from '../catalogue';
import { visibleCoachSteps } from '../coach';
import { coachEntries, continueCoach, startCoach, type CoachEntry } from '../coaching';
import type { GuidanceInternals } from '../context';
import { availableTours, pageSteps, toursForPage, visibleSteps } from '../eligibility';
import { HELP_PANEL_ID } from '../engine/dom';
import type { TourStatus } from '../progress/store';
import { useGuidanceState } from '../store';
import type { GuidanceKey, TourDefinition } from '../types';

/**
 * The Help panel (brief §7; WC-11): an R-050 Sheet — from the inline end on a
 * desktop, from the bottom on a phone. First, where there is one, "Guide me on
 * this page" (the most contextual help, in reach on a phone without
 * scrolling); then three groups:
 *
 * - **Guided workflows** (Layer B): the coaches this person may take, which
 *   walk a real task step by step while the person does every step;
 * - **Get oriented** (Layer A): the portal's short orientation;
 * - **Screen guides**: the task guides that explain a screen without acting.
 *
 * Each item has its status and one action (Start, Continue, Restart — or Show
 * coach for the one running now), a readable list of its steps (the accessible
 * fallback of the overlay), and the note that progress is kept in this browser
 * only.
 *
 * Starting a guide closes the panel first; focus goes back to where the panel
 * was opened from, and the guide's card takes it from there.
 *
 * One clear primary action (TOUR-06, DS-09): the coach running or in
 * progress, else the guide in progress, else the orientation for a newcomer,
 * else the first coach not started. Every other action is secondary.
 */
const STATUS: Record<TourStatus, { key: GuidanceKey; tone: StatusTone }> = {
  not_started: { key: 'guidance.help.status.notStarted', tone: 'neutral' },
  in_progress: { key: 'guidance.help.status.inProgress', tone: 'progress' },
  completed: { key: 'guidance.help.status.completed', tone: 'completed' },
};

type Lead = { kind: 'coach'; id: string } | { kind: 'tour'; id: string } | null;

export function HelpPanel({ guidance }: { guidance: GuidanceInternals }) {
  const { t } = useTranslation();
  const phone = useMediaQuery(PHONE_QUERY);
  const { panelOpen } = useGuidanceState(guidance.store);
  // Re-render when stored progress changes (a guide finished in this session).
  useSyncExternalStore(guidance.progress.subscribe, guidance.progress.get, guidance.progress.get);
  useReturnFocus(guidance, panelOpen);

  const { ctx, catalogue, page, portal, progress, store } = guidance;
  const tours = availableTours(catalogue, ctx);
  const orientation = tours.filter((def) => def.id === ORIENTATION[portal]);
  const taskGuides = tours.filter((def) => def.id !== ORIENTATION[portal]);
  const here = toursForPage(catalogue, ctx, page?.id ?? null);
  const coaches = panelOpen ? coachEntries(guidance) : [];

  const lead: Lead = (() => {
    const coach =
      coaches.find((entry) => entry.running) ??
      coaches.find((entry) => entry.status === 'in_progress');

    if (coach !== undefined) return { kind: 'coach', id: coach.def.id };

    const tour =
      tours.find((def) => progress.status(def) === 'in_progress') ??
      orientation.find((def) => progress.status(def) === 'not_started');

    if (tour !== undefined) return { kind: 'tour', id: tour.id };

    const fresh = coaches.find((entry) => entry.status === 'not_started');

    if (fresh !== undefined) return { kind: 'coach', id: fresh.def.id };

    // As before coaches: the first guide not started.
    const next = tours.find((def) => progress.status(def) === 'not_started');

    return next === undefined ? null : { kind: 'tour', id: next.id };
  })();

  const start = (def: TourDefinition, mode: 'resume' | 'restart') => {
    const steps = visibleSteps(def, ctx);
    const stored = mode === 'resume' ? progress.resumeStep(def) : undefined;
    const from = steps.find((step) => step.id === stored) ?? steps[0];

    store.start({ tourId: def.id, mode: 'full', stepId: from.id });
  };

  const guideThisPage = (def: TourDefinition) => {
    if (page === null) return;

    const first = pageSteps(def, ctx, page.id).at(0);

    if (first !== undefined)
      store.start({ tourId: def.id, mode: 'page', page: page.id, stepId: first.id });
  };

  const showCoach = () => {
    store.setMinimised(false);
    store.closePanel();
  };

  const tourItem = (def: TourDefinition) => (
    <TourItem
      key={def.id}
      def={def}
      status={progress.status(def)}
      primary={lead?.kind === 'tour' && lead.id === def.id}
      steps={visibleSteps(def, ctx).map((step) => ({
        id: step.id,
        title: t(step.titleKey),
        body: t(step.bodyKey),
      }))}
      onStart={(mode) => start(def, mode)}
    />
  );

  return (
    <Sheet
      id={HELP_PANEL_ID}
      open={panelOpen}
      title={t('guidance.help.panelTitle')}
      onClose={() => store.closePanel()}
      side={phone ? 'bottom' : 'end'}
    >
      {/*
        Only while open (W2's finding): a closed native <dialog> keeps its
        children in the DOM, so every page would carry the guides' titles and
        step lists, hidden, outside <main> — pointless weight, and text that
        collides with a page's own (strict "getByText" matches it twice). The
        dialog itself stays, for `aria-controls` and focus return.
      */}
      {panelOpen ? (
        <div className="space-y-6 p-4">
          <p className="type-ui text-ink">{t('guidance.help.intro')}</p>

          {/*
            The page's own guide first, where there is one: the most contextual
            help stays in reach without scrolling on a phone (w6-to-w1-4), above
            the three groups.
          */}
          {here.length > 0 ? (
            <Group
              id="guidance-this-page"
              title={t('guidance.help.thisPage')}
              hint={t('guidance.help.thisPageHint')}
            >
              <ul className="flex flex-wrap gap-2">
                {here.map((def) => (
                  <li key={def.id}>
                    <Button variant="secondary" size="sm" onClick={() => guideThisPage(def)}>
                      {t('guidance.help.guideWith', { title: t(def.titleKey) })}
                    </Button>
                  </li>
                ))}
              </ul>
            </Group>
          ) : null}

          {coaches.length > 0 ? (
            <Group
              id="guidance-workflows"
              title={t('guidance.help.groups.workflows')}
              hint={t('guidance.help.groups.workflowsHint')}
            >
              <ul className="space-y-3">
                {coaches.map((entry) => (
                  <CoachItem
                    key={entry.def.id}
                    entry={entry}
                    primary={lead?.kind === 'coach' && lead.id === entry.def.id}
                    steps={visibleCoachSteps(entry.def, ctx).map((step) => ({
                      id: step.id,
                      title: t(step.titleKey),
                      body: t(step.bodyKey),
                    }))}
                    onShow={showCoach}
                    onStart={() => startCoach(guidance, entry.def.id)}
                    onContinue={() => continueCoach(guidance, entry.def.id)}
                  />
                ))}
              </ul>
            </Group>
          ) : null}

          {orientation.length > 0 ? (
            <Group id="guidance-oriented" title={t('guidance.help.groups.oriented')}>
              <ul className="space-y-3">{orientation.map(tourItem)}</ul>
            </Group>
          ) : null}

          {taskGuides.length > 0 ? (
            <Group
              id="guidance-explain"
              title={t('guidance.help.groups.explain')}
              hint={t('guidance.help.groups.explainHint')}
            >
              <ul className="space-y-3">{taskGuides.map(tourItem)}</ul>
            </Group>
          ) : null}

          {coaches.length === 0 && tours.length === 0 ? (
            <p className="type-meta text-ink-muted">{t('guidance.help.none')}</p>
          ) : null}

          <p className="type-meta text-ink-muted">{t('guidance.help.browserOnly')}</p>
        </div>
      ) : null}
    </Sheet>
  );
}

/**
 * `openPanel({ returnFocusTo })`: when the panel closes without a guide
 * starting, focus goes to the control that asked for it (an account menu's
 * trigger, say, whose own item has gone) — after the dialog's own return.
 */
function useReturnFocus(guidance: GuidanceInternals, panelOpen: boolean): void {
  const wasOpen = useRef(false);

  useEffect(() => {
    if (panelOpen) {
      wasOpen.current = true;

      return;
    }

    if (!wasOpen.current) return;

    wasOpen.current = false;

    const element = guidance.store.takePanelReturn();
    const dialog = document.getElementById(HELP_PANEL_ID);

    // A guide just started from the panel: it owns focus, and returns it there.
    if (element === null || guidance.store.getState().active?.shown === false) return;

    const focus = () => {
      if (element.isConnected) element.focus();
    };

    // The native dialog restores its opener on `close`; this runs after it.
    if (dialog !== null)
      dialog.addEventListener('close', () => setTimeout(focus, 0), { once: true });
    else focus();
  }, [guidance, panelOpen]);
}

function Group({
  id,
  title,
  hint,
  children,
}: {
  id: string;
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div>
        <h3 id={id} className="type-card-title text-ink-heading">
          {title}
        </h3>
        {hint ? <p className="type-meta mt-0.5 text-ink-muted">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

interface StepLine {
  id: string;
  title: string;
  body: string;
}

function StepList({ steps }: { steps: readonly StepLine[] }) {
  const { t } = useTranslation();

  return (
    <details className="group mt-3">
      {/*
        A disclosure, and it looks like one: its own chevron (the browser's
        marker is gone with `flex`), pointing to the inline end when closed and
        down when open — mirrored in Arabic, so it turns the other way. 44px tall.
      */}
      <summary className="type-meta flex min-h-11 cursor-pointer list-none items-center gap-1.5 font-medium text-ink [&::-webkit-details-marker]:hidden">
        <Icon
          name="chevron"
          size={16}
          className="shrink-0 text-ink-muted transition-transform group-open:rotate-90 motion-reduce:transition-none rtl:-scale-x-100 rtl:group-open:-rotate-90"
        />
        {t('guidance.help.steps', { count: steps.length })}
      </summary>
      <ol className="type-meta list-decimal space-y-2 ps-5 text-ink">
        {steps.map((step) => (
          <li key={step.id}>
            <span className="font-semibold">{step.title}</span>
            <span className="block text-ink-muted">{step.body}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}

function TourItem({
  def,
  status,
  primary,
  steps,
  onStart,
}: {
  def: TourDefinition;
  status: TourStatus;
  primary: boolean;
  steps: readonly StepLine[];
  onStart: (mode: 'resume' | 'restart') => void;
}) {
  const { t } = useTranslation();
  const title = t(def.titleKey);

  return (
    <li className="rounded-(--radius-card) border border-line-subtle bg-card p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="type-ui font-semibold text-ink-heading">{title}</h4>
        <StatusBadge status={status} label={t(STATUS[status].key)} tone={STATUS[status].tone} />
      </div>
      <p className="type-meta mt-1 text-ink-muted">{t(def.summaryKey)}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {status === 'in_progress' ? (
          <Button
            size="sm"
            variant={primary ? 'primary' : 'secondary'}
            onClick={() => onStart('resume')}
            aria-label={t('guidance.help.continueNamed', { title })}
          >
            {t('guidance.help.continue')}
          </Button>
        ) : (
          <Button
            size="sm"
            variant={primary ? 'primary' : 'secondary'}
            onClick={() => onStart('restart')}
            aria-label={t(
              status === 'completed' ? 'guidance.help.restartNamed' : 'guidance.help.startNamed',
              { title },
            )}
          >
            {t(status === 'completed' ? 'guidance.help.restart' : 'guidance.help.start')}
          </Button>
        )}
      </div>

      <StepList steps={steps} />
    </li>
  );
}

function CoachItem({
  entry,
  primary,
  steps,
  onShow,
  onStart,
  onContinue,
}: {
  entry: CoachEntry;
  primary: boolean;
  steps: readonly StepLine[];
  onShow: () => void;
  onStart: () => void;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const { def, status, running } = entry;
  const title = t(def.titleKey);
  const variant = primary ? 'primary' : 'secondary';

  return (
    <li className="rounded-(--radius-card) border border-line-subtle bg-card p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h4 className="type-ui font-semibold text-ink-heading">{title}</h4>
        {running ? (
          <StatusBadge
            status="in_progress"
            label={t('guidance.help.status.running')}
            tone="progress"
          />
        ) : (
          <StatusBadge status={status} label={t(STATUS[status].key)} tone={STATUS[status].tone} />
        )}
      </div>
      <p className="type-meta mt-1 text-ink-muted">{t(def.summaryKey)}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {running ? (
          <Button
            size="sm"
            variant={variant}
            onClick={onShow}
            aria-label={t('guidance.help.showNamed', { title })}
          >
            {t('guidance.help.show')}
          </Button>
        ) : status === 'in_progress' ? (
          <>
            <Button
              size="sm"
              variant={variant}
              onClick={onContinue}
              aria-label={t('guidance.help.continueNamed', { title })}
            >
              {t('guidance.help.continue')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onStart}
              aria-label={t('guidance.help.restartNamed', { title })}
            >
              {t('guidance.help.restart')}
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant={variant}
            onClick={onStart}
            aria-label={t(
              status === 'completed' ? 'guidance.help.restartNamed' : 'guidance.help.startNamed',
              { title },
            )}
          >
            {t(status === 'completed' ? 'guidance.help.restart' : 'guidance.help.start')}
          </Button>
        )}
      </div>
      {status !== 'not_started' || running ? (
        <p className="type-meta mt-2 text-ink-muted">{t('guidance.help.restartNote')}</p>
      ) : null}

      <StepList steps={steps} />
    </li>
  );
}
